export interface ReleaseNoteSection {
  title: string;
  icon: string;
  tone: 'primary' | 'success' | 'warning';
  items: string[];
  visibleToRoles?: string[];
}

export interface ReleaseNote {
  version: string;
  releasedAt: string;
  title: string;
  summary: string;
  sections: ReleaseNoteSection[];
  important?: string[];
}

export const RELEASE_NOTES: Record<string, ReleaseNote> = {
  '1.3.4': {
    version: '1.3.4',
    releasedAt: '2026-10-14',
    title: 'Toutes les entrées ouvrent la gestion',
    summary: 'La boutique publique est désactivée sur le frontend. Ses anciennes adresses ouvrent automatiquement l’espace de gestion.',
    sections: [
      {
        title: 'Nouvelles fonctionnalités',
        icon: 'fa-star',
        tone: 'primary',
        items: [
          'Aucune nouvelle fonctionnalité dans cette mise à jour.',
        ],
      },
      {
        title: 'Améliorations',
        icon: 'fa-wand-magic-sparkles',
        tone: 'success',
        items: [
          'L’accueil et les anciens liens de la boutique ouvrent la connexion à la gestion ou l’espace du personnel déjà connecté.',
          'Les logos ramènent à la gestion et le raccourci vers la boutique en ligne est retiré.',
        ],
      },
      {
        title: 'Correctifs',
        icon: 'fa-screwdriver-wrench',
        tone: 'warning',
        visibleToRoles: ['support', 'superadmin', 'super_admin'],
        items: [
          'Redirection des pages publiques indépendante de l’activation du module e-commerce sur le serveur.',
          'Les paramètres des anciens liens clients ne sont pas transmis au backoffice ; la récupération du mot de passe du personnel reste accessible.',
        ],
      },
    ],
  },
  '1.3.3': {
    version: '1.3.3',
    releasedAt: '2026-10-14',
    title: 'Un accès direct à la gestion depuis l’accueil',
    summary: 'L’accueil ouvre désormais la gestion lorsque la boutique en ligne est désactivée.',
    sections: [
      {
        title: 'Nouvelles fonctionnalités',
        icon: 'fa-star',
        tone: 'primary',
        items: [
          'Un lien permet d’accéder à la gestion depuis la page « Boutique indisponible ».',
        ],
      },
      {
        title: 'Améliorations',
        icon: 'fa-wand-magic-sparkles',
        tone: 'success',
        items: [
          'Ouverture de la connexion à la gestion depuis l’accueil lorsque le module e-commerce est désactivé.',
          'Les utilisateurs déjà connectés retrouvent leur espace selon leur profil.',
        ],
      },
      {
        title: 'Correctifs',
        icon: 'fa-screwdriver-wrench',
        tone: 'warning',
        visibleToRoles: ['support', 'superadmin', 'super_admin'],
        items: [
          'Correction de la redirection de l’accueil vers « Boutique indisponible » sur les installations sans e-commerce.',
          'Conservation du catalogue à l’accueil lorsque l’e-commerce est activé et de la nouvelle tentative en cas d’erreur de configuration.',
        ],
      },
    ],
  },
  '1.3.2': {
    version: '1.3.2',
    releasedAt: '2026-10-13',
    title: 'Le menu utilisateur accessible sur smartphone et tablette',
    summary: 'Cette version corrige le blocage du menu déroulant de l’utilisateur connecté sur les petits appareils.',
    sections: [
      {
        title: 'Nouvelles fonctionnalités',
        icon: 'fa-star',
        tone: 'primary',
        items: [
          'Aucune nouvelle fonctionnalité dans cette mise à jour corrective.',
        ],
      },
      {
        title: 'Améliorations',
        icon: 'fa-wand-magic-sparkles',
        tone: 'success',
        items: [
          'Accès rétabli au profil, au manuel d’utilisation et à la déconnexion depuis le menu utilisateur sur smartphone et tablette.',
        ],
      },
      {
        title: 'Correctifs',
        icon: 'fa-screwdriver-wrench',
        tone: 'warning',
        visibleToRoles: ['support', 'superadmin', 'super_admin'],
        items: [
          'Correction de la fermeture immédiate du menu utilisateur après un appui dans les interfaces d’administration et de caisse.',
          'Ouverture et fermeture au clic rétablies, avec conservation du survol sur ordinateur.',
        ],
      },
    ],
  },
  '1.3.1': {
    version: '1.3.1',
    releasedAt: '2026-09-03',
    title: 'Des traitements groupés plus rapides et mieux contrôlés',
    summary: 'Cette version ajoute la sélection multi-page et sécurise les actions groupées sur les principales listes de gestion.',
    sections: [
      {
        title: 'Nouvelles fonctionnalités',
        icon: 'fa-star',
        tone: 'primary',
        items: [
          'Sélection multiple persistante entre les pages, recherches, tris et filtres des tableaux compatibles.',
          'Actions groupées d’activation, de désactivation et de suppression avec confirmation du nombre de lignes.',
          'Affectation simultanée de plusieurs produits à une catégorie ou à un rayon.',
        ],
      },
      {
        title: 'Améliorations',
        icon: 'fa-wand-magic-sparkles',
        tone: 'success',
        items: [
          'Bilan détaillé après traitement avec conservation automatique des lignes restées en échec.',
          'Protection des lignes système, des documents finalisés et des données appartenant à un autre site.',
          'Limite explicite de 500 lignes par action pour conserver des traitements rapides et lisibles.',
        ],
      },
      {
        title: 'Correctifs',
        icon: 'fa-screwdriver-wrench',
        tone: 'warning',
        visibleToRoles: ['support', 'superadmin', 'super_admin'],
        items: [
          'Rattachement fiable des anciens produits au catalogue lors d’une activation groupée.',
          'Navigation de ligne neutralisée lors de l’utilisation des cases de sélection au clic ou au clavier.',
          'Exports, impressions et réglages de colonnes préservés sans exposer la colonne technique de sélection.',
        ],
      },
    ],
  },
  '1.3.0': {
    version: '1.3.0',
    releasedAt: '2026-08-20',
    title: 'Une gestion plus complète, plus claire et plus mobile',
    summary: 'Cette version enrichit la caisse, le pilotage multisite, le suivi des stocks et les outils de décision.',
    sections: [
      {
        title: 'Nouvelles fonctionnalités',
        icon: 'fa-star',
        tone: 'primary',
        items: [
          'Portefeuilles électroniques : recharges, dépôts, retraits, ajustements, historique et rapport dédié.',
          'Nouveaux tableaux de bord pour les portefeuilles, les expirations et le réapprovisionnement.',
          'Calendrier hebdomadaire des ventes, achats, mouvements de stock et expirations.',
          'Rapport chiffre d’affaires et marges par produit sur la période sélectionnée.',
          'Gestion globale des produits et des utilisateurs depuis la liste des boutiques.',
        ],
      },
      {
        title: 'Améliorations',
        icon: 'fa-wand-magic-sparkles',
        tone: 'success',
        items: [
          'Interface harmonisée : tableaux, filtres, sélecteurs, onglets, boutons et cartes du dashboard.',
          'Sélection des colonnes visibles prise en compte dans les tableaux et les exports Excel.',
          'Accès multisite simplifié pour les utilisateurs autorisés et meilleure séparation des boutiques.',
          'Fin de journée enrichie avec le billetage, les transactions portefeuille et une date initiale intelligente.',
          'Produits électroniques à montant libre, sans prix fixe ni contrainte de stock physique.',
        ],
      },
      {
        title: 'Correctifs',
        icon: 'fa-screwdriver-wrench',
        tone: 'warning',
        visibleToRoles: ['support', 'superadmin', 'super_admin'],
        items: [
          'Synchronisation fiable du réglage de billetage entre le backoffice et la caisse.',
          'Correction de l’affichage et du filtrage des portefeuilles électroniques et de leur rapport.',
          'Association des ventes électroniques au véritable client système de la boutique.',
          'Correction des accès, permissions et contrôles de contexte pour les utilisateurs multisites.',
          'Amélioration de la stabilité du cache applicatif, des notifications et des vues de rapports.',
        ],
      },
    ],
    important: [
      'Les produits électroniques doivent être associés à un portefeuille actif avant leur utilisation en caisse.',
      'Les permissions des utilisateurs restent appliquées séparément dans chaque module.',
    ],
  },
};

const versionParts = (version: string): number[] => version.split('-', 1)[0]
  .split('.')
  .map((part) => Number(part) || 0);

export const isVersionNewer = (current: string, acknowledged: string): boolean => {
  if (!current) return false;
  if (!acknowledged) return true;

  const currentParts = versionParts(current);
  const acknowledgedParts = versionParts(acknowledged);
  const length = Math.max(currentParts.length, acknowledgedParts.length);

  for (let index = 0; index < length; index += 1) {
    const difference = (currentParts[index] || 0) - (acknowledgedParts[index] || 0);
    if (difference !== 0) return difference > 0;
  }

  return current !== acknowledged && !current.includes('-') && acknowledged.includes('-');
};

export const releaseNoteFor = (version: string): ReleaseNote | null => RELEASE_NOTES[version] || null;
